import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import { DEFAULT_SEO_SETTINGS, mergeSeoSettings, type SeoSettings } from '../lib/seo';

type SeoHeadProps = {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  noIndex?: boolean;
  structuredData?: Record<string, unknown> | Array<Record<string, unknown>>;
};

let seoCache: SeoSettings | null = null;
let seoPromise: Promise<SeoSettings> | null = null;

function safeTrim(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeTitleSuffix(value: unknown): string {
  const raw = safeTrim(value);
  if (!raw) return '';
  if (raw.startsWith('|')) return ` ${raw}`;
  return raw.startsWith('-') ? ` ${raw}` : ` | ${raw}`;
}

function setMeta(name: string, content: string, useProperty = false) {
  const selector = useProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  let tag = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement('meta');
    if (useProperty) tag.setAttribute('property', name);
    else tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function setCanonical(url: string) {
  let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', url);
}

function upsertJsonLd(id: string, payload: Record<string, unknown>) {
  const selector = `script[type="application/ld+json"][data-seo-jsonld="${id}"]`;
  let script = document.head.querySelector(selector) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-jsonld', id);
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(payload);
}

function readSiteUrl(settings: SeoSettings): string {
  const siteUrl = safeTrim(settings.site_url);
  if (siteUrl) {
    return siteUrl.replace(/\/$/, '');
  }
  return window.location.origin;
}

async function loadSeoSettings(): Promise<SeoSettings> {
  if (seoCache) return seoCache;
  if (seoPromise) return seoPromise;

  seoPromise = apiFetch('/api/settings/seo')
    .then(async (res) => {
      if (!res.ok) return DEFAULT_SEO_SETTINGS;
      return mergeSeoSettings(await res.json());
    })
    .catch(() => DEFAULT_SEO_SETTINGS)
    .finally(() => {
      seoPromise = null;
    });

  seoCache = await seoPromise;
  return seoCache;
}

export default function SeoHead({ title, description, path = '/', image, noIndex = false, structuredData }: SeoHeadProps) {
  const [settings, setSettings] = useState<SeoSettings>(DEFAULT_SEO_SETTINGS);

  useEffect(() => {
    loadSeoSettings().then(setSettings).catch(() => setSettings(DEFAULT_SEO_SETTINGS));
  }, []);

  useEffect(() => {
    const siteUrl = readSiteUrl(settings);
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    const canonical = `${siteUrl}${cleanPath === '/' ? '' : cleanPath}`;

    const pageTitle = safeTrim(title) || settings.default_title;
    const titleSuffix = normalizeTitleSuffix(settings.title_suffix);
    const resolvedTitle = pageTitle.includes(settings.site_name) || !titleSuffix
      ? pageTitle
      : `${pageTitle}${titleSuffix}`;
    const resolvedDescription = safeTrim(description) || settings.default_description;
    const resolvedImage = safeTrim(image) || settings.default_og_image;
    const robots = (settings.robots_index && !noIndex ? 'index' : 'noindex') + ',' + (settings.robots_follow ? 'follow' : 'nofollow');

    document.title = resolvedTitle;
    setMeta('description', resolvedDescription);
    setMeta('keywords', settings.default_keywords || '');
    setMeta('robots', robots);

    setMeta('og:title', resolvedTitle, true);
    setMeta('og:description', resolvedDescription, true);
    setMeta('og:type', 'website', true);
    setMeta('og:url', canonical, true);
    if (resolvedImage) setMeta('og:image', resolvedImage, true);

    setMeta('twitter:card', resolvedImage ? 'summary_large_image' : 'summary');
    setMeta('twitter:title', resolvedTitle);
    setMeta('twitter:description', resolvedDescription);
    const twitterHandle = safeTrim(settings.twitter_handle);
    if (twitterHandle) setMeta('twitter:site', twitterHandle);
    if (resolvedImage) setMeta('twitter:image', resolvedImage);

    setCanonical(canonical);

    const organizationId = `${siteUrl}/#organization`;
    const websiteId = `${siteUrl}/#website`;
    const webPageId = `${canonical}#webpage`;

    const baseSchemas: Array<{ id: string; data: Record<string, unknown> }> = [
      {
        id: 'organization',
        data: {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          '@id': organizationId,
          name: settings.site_name || 'Ahime',
          url: siteUrl,
          logo: `${siteUrl}/logo.png`,
        },
      },
      {
        id: 'website',
        data: {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          '@id': websiteId,
          url: siteUrl,
          name: settings.site_name || 'Ahime',
          inLanguage: 'fr-FR',
          publisher: { '@id': organizationId },
        },
      },
      {
        id: 'webpage',
        data: {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          '@id': webPageId,
          url: canonical,
          name: resolvedTitle,
          description: resolvedDescription,
          isPartOf: { '@id': websiteId },
        },
      },
    ];

    const customSchemas = (Array.isArray(structuredData) ? structuredData : structuredData ? [structuredData] : []).map((entry, index) => ({
      id: `custom-${index}`,
      data: entry,
    }));

    const allSchemas = [...baseSchemas, ...customSchemas];
    allSchemas.forEach((schema) => upsertJsonLd(schema.id, schema.data));

    const activeIds = new Set(allSchemas.map((schema) => schema.id));
    const staleNodes = document.head.querySelectorAll('script[type="application/ld+json"][data-seo-jsonld]');
    staleNodes.forEach((node) => {
      const id = node.getAttribute('data-seo-jsonld');
      if (!id || activeIds.has(id)) return;
      node.remove();
    });
  }, [settings, title, description, path, image, noIndex, structuredData]);

  return null;
}
